import type { CapabilityPlan } from '@/lib/capabilities/types';

export type FeatureId =
  | 'capability.browser'
  | 'capability.sandbox'
  | 'media.premiumModels'
  | 'media.image.highResolution'
  | 'media.image.transparentBackground'
  | 'media.image.referenceImages'
  | 'media.model3d.generation'
  | 'media.model3d.texturedMesh';

export interface FeatureAccessRule {
  label: string;
  enabled: boolean;
  requiredPlan?: CapabilityPlan;
}

export type FeatureAccessConfig = Record<FeatureId, FeatureAccessRule>;

// Product-access code flags. Flip these here when a feature moves between
// "available to everyone" and plan-gated. Keep this as the single source of
// truth for code-level feature restrictions.
const OPEN_ALL_FEATURES_TO_ALL = false;
const OPEN_BROWSER_AUTOMATION_TO_ALL = true;
const OPEN_SANDBOX_TO_ALL = false;
const OPEN_PREMIUM_MEDIA_MODELS_TO_ALL = false;
const OPEN_ADVANCED_IMAGE_CONTROLS_TO_ALL = false;
const OPEN_3D_GENERATION_TO_ALL = false;

interface FeatureAccessFlags {
  openAllFeaturesToAll: boolean;
  openBrowserAutomationToAll: boolean;
  openSandboxToAll: boolean;
  openPremiumMediaModelsToAll: boolean;
  openAdvancedImageControlsToAll: boolean;
  open3dGenerationToAll: boolean;
}

const DEFAULT_FEATURE_ACCESS_FLAGS: FeatureAccessFlags = {
  openAllFeaturesToAll: OPEN_ALL_FEATURES_TO_ALL,
  openBrowserAutomationToAll: OPEN_BROWSER_AUTOMATION_TO_ALL,
  openSandboxToAll: OPEN_SANDBOX_TO_ALL,
  openPremiumMediaModelsToAll: OPEN_PREMIUM_MEDIA_MODELS_TO_ALL,
  openAdvancedImageControlsToAll: OPEN_ADVANCED_IMAGE_CONTROLS_TO_ALL,
  open3dGenerationToAll: OPEN_3D_GENERATION_TO_ALL,
};

function planGate(openToAll: boolean, requiredPlan: CapabilityPlan = 'pro') {
  return openToAll ? undefined : requiredPlan;
}

export function createFeatureAccessConfig(flags: FeatureAccessFlags): FeatureAccessConfig {
  const all = flags.openAllFeaturesToAll;
  const advancedImagesOpen = all || flags.openAdvancedImageControlsToAll;
  const model3dOpen = all || flags.open3dGenerationToAll;

  return {
    'capability.browser': {
      label: 'Browser Automation',
      enabled: true,
      requiredPlan: planGate(all || flags.openBrowserAutomationToAll),
    },
    'capability.sandbox': {
      label: 'Code Sandbox',
      enabled: true,
      requiredPlan: planGate(all || flags.openSandboxToAll),
    },
    'media.premiumModels': {
      label: 'Premium media models',
      enabled: true,
      requiredPlan: planGate(all || flags.openPremiumMediaModelsToAll),
    },
    'media.image.highResolution': {
      label: 'High-resolution image output',
      enabled: true,
      requiredPlan: planGate(advancedImagesOpen),
    },
    'media.image.transparentBackground': {
      label: 'Transparent image output',
      enabled: true,
      requiredPlan: planGate(advancedImagesOpen),
    },
    'media.image.referenceImages': {
      label: 'Reference image workflows',
      enabled: true,
      requiredPlan: planGate(advancedImagesOpen),
    },
    'media.model3d.generation': {
      label: '3D model generation',
      enabled: true,
      requiredPlan: planGate(model3dOpen),
    },
    'media.model3d.texturedMesh': {
      label: 'Textured 3D mesh generation',
      enabled: true,
      requiredPlan: planGate(model3dOpen),
    },
  };
}

export const FEATURE_ACCESS_PRESETS = {
  default: createFeatureAccessConfig(DEFAULT_FEATURE_ACCESS_FLAGS),
  openToAll: createFeatureAccessConfig({
    openAllFeaturesToAll: true,
    openBrowserAutomationToAll: true,
    openSandboxToAll: true,
    openPremiumMediaModelsToAll: true,
    openAdvancedImageControlsToAll: true,
    open3dGenerationToAll: true,
  }),
  proGated: createFeatureAccessConfig({
    openAllFeaturesToAll: false,
    openBrowserAutomationToAll: false,
    openSandboxToAll: false,
    openPremiumMediaModelsToAll: false,
    openAdvancedImageControlsToAll: false,
    open3dGenerationToAll: false,
  }),
} as const satisfies Record<string, FeatureAccessConfig>;

export const ACTIVE_FEATURE_ACCESS_PRESET: keyof typeof FEATURE_ACCESS_PRESETS = 'openToAll';
export const FEATURE_ACCESS = FEATURE_ACCESS_PRESETS[ACTIVE_FEATURE_ACCESS_PRESET];

const planRank: Record<CapabilityPlan, number> = {
  free: 0,
  pro: 1,
  team: 2,
};

export function normalizeFeaturePlan(plan: string | null | undefined): CapabilityPlan {
  if (plan === 'team') return 'team';
  if (plan === 'pro') return 'pro';
  return 'free';
}

export function getFeatureAccessRule(featureId: FeatureId) {
  return FEATURE_ACCESS[featureId];
}

export function getFeatureRequiredPlan(featureId: FeatureId) {
  const rule = getFeatureAccessRule(featureId);
  return rule.enabled ? rule.requiredPlan : undefined;
}

export function isFeaturePlanGated(featureId: FeatureId) {
  return Boolean(getFeatureRequiredPlan(featureId));
}

export function isFeatureAllowedForPlan(featureId: FeatureId, plan: string | null | undefined) {
  const rule = getFeatureAccessRule(featureId);
  if (!rule.enabled) return false;
  if (!rule.requiredPlan) return true;
  return planRank[normalizeFeaturePlan(plan)] >= planRank[rule.requiredPlan];
}

export function areAllFeaturesOpenToAll() {
  return ACTIVE_FEATURE_ACCESS_PRESET === 'openToAll';
}
